import { type TaskCreateDto, useSalesCompleteTask, useSalesGetProspects } from "@crowdcoursing/api/openapi";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";

import { useTaskDashboard } from "~hooks/useTaskDashboard";
import { useToast } from "~hooks/useToast";
import { ProspectDetailsSlideout } from "~ui/templates/ProspectDetailsSlideout/ProspectDetailsSlideout";
import { TaskCalendarTemplate } from "~ui/templates/TaskCalendarTemplate/TaskCalendarTemplate";
import { type CalendarView, mergeSalesTasksForCalendar } from "~ui/templates/TaskCalendarTemplate/taskCalendarUtils";
import {
  type CarouselTaskItem,
  combineTasks,
  type ProspectEnrichmentData,
} from "~ui/templates/TaskDashboardTemplate/taskUtils";

export const TaskCalendarPage = () => {
  const { salesTasks, roomTasks, eventTasks } = useTaskDashboard();
  const { data: allProspects, isLoading: isLoadingProspects } = useSalesGetProspects({});
  const queryClient = useQueryClient();
  const toast = useToast();

  const [view, setView] = useState<CalendarView>("week");
  const [selectedDate, setSelectedDate] = useState(() => DateTime.now().startOf("day"));
  const [slideout, setSlideout] = useState<{ prospectId: string; task: CarouselTaskItem } | null>(null);

  const { isPending: isCompletingSlideoutTask, mutateAsync: completeSalesTask } = useSalesCompleteTask({
    onError: () => {
      toast.error("Failed to complete task");
    },
    onSuccess: async () => {
      toast.success("Task completed");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales", "tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales", "reports", "tasks", "due-soon"] }),
      ]);
    },
  });

  const handleCompleteSlideoutTask = useCallback(
    async (task: CarouselTaskItem) => {
      if (!task.id) {
        return;
      }
      if (task.type === "sales") {
        await completeSalesTask({ pathParams: { taskId: task.id } });
        setSlideout(null);
        return;
      }
      toast.error("Completing this task type is not supported yet from the calendar.");
    },
    [completeSalesTask, toast],
  );

  const handleSalesTaskUpdatedFromSlideout = useCallback(
    ({ body, taskId }: { body: TaskCreateDto; taskId: string }) => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales", "tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales", "reports", "tasks", "due-soon"] }),
      ]);
      setSlideout((s) => (s?.task.id === taskId ? { ...s, task: { ...s.task, ...body } as CarouselTaskItem } : s));
    },
    [queryClient],
  );

  const prospectsMap = useMemo(() => {
    if (!allProspects) {
      return undefined;
    }
    const map = new Map<string, ProspectEnrichmentData>();
    for (const prospect of allProspects) {
      if (prospect.id) {
        map.set(prospect.id, {
          id: prospect.id,
          name: prospect.name,
          region: prospect.region,
          timeZone: prospect.timeZone,
        });
      }
    }
    return map;
  }, [allProspects]);

  const mergedSalesTasks = useMemo(
    () => mergeSalesTasksForCalendar(salesTasks.myTasks, salesTasks.tasksDueSoon),
    [salesTasks.myTasks, salesTasks.tasksDueSoon],
  );

  const tasks = useMemo(
    () => combineTasks(mergedSalesTasks, roomTasks.myTasks, eventTasks.myTasks, prospectsMap),
    [mergedSalesTasks, roomTasks.myTasks, eventTasks.myTasks, prospectsMap],
  );

  const isLoading = salesTasks.isLoading || roomTasks.isLoading || eventTasks.isLoading || isLoadingProspects;

  if (isLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: "100vh", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TaskCalendarTemplate
        view={view}
        onViewChange={setView}
        selectedDate={selectedDate}
        onSelectedDateChange={setSelectedDate}
        tasks={tasks}
        onOpenProspectFromTask={(task) => {
          if (task.prospectId) {
            setSlideout({ prospectId: task.prospectId, task });
          }
        }}
      />
      <ProspectDetailsSlideout
        contextTask={slideout?.task}
        isCompletingContextTask={isCompletingSlideoutTask}
        prospectId={slideout?.prospectId ?? ""}
        onClose={() => setSlideout(null)}
        onCompleteContextTask={handleCompleteSlideoutTask}
        onSalesTaskUpdated={handleSalesTaskUpdatedFromSlideout}
      />
    </>
  );
};
